import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TabPanel from '../components/TabPanel';
import HtmlContent from '../components/HtmlContent';
import LocalizationService from '../../application/services/LocalizationService';
import {
  ArrowLeft,
  Shield,
  FileText,
  EyeOff,
  Users,
  Image,
  Cookie,
  Copyright,
  ShieldCheck,
  Calendar,
  Clock,
} from 'lucide-react';
import './TermsPage.css';

// Section arayüzünü map içinde kullanarak uyarıyı kaldırıyorum
interface Section {
  title: string;
  content: string;
}

interface TermsPageProps {
  defaultTab?: string;
}

// Tab icon mapping
const TAB_ICONS: Record<string, React.ReactNode> = {
  terms:              <FileText size={16} />,
  privacy:            <EyeOff size={16} />,
  communityGuidelines:<Users size={16} />,
  contentPolicy:      <Image size={16} />,
  cookies:            <Cookie size={16} />,
  copyright:          <Copyright size={16} />,
  childSafety:        <ShieldCheck size={16} />,
};

function TermsPage({ defaultTab = 'terms' }: TermsPageProps) {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [currentLanguage, setCurrentLanguage] = useState(LocalizationService.getCurrentLanguage());
  const isEnglish = currentLanguage === 'en';

  // Dil değişikliklerini izle
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = LocalizationService.getCurrentLanguage();
      if (newLanguage !== currentLanguage) {
        setCurrentLanguage(newLanguage);
      }
    };
    const i18n = LocalizationService.getI18nInstance();
    i18n.on('languageChanged', handleLanguageChange);
    return () => { i18n.off('languageChanged', handleLanguageChange); };
  }, [currentLanguage]);

  // Tab bilgileri
  const tabs = [
    { id: 'terms',              label: isEnglish ? 'Terms of Use'         : 'Kullanım Koşulları',  path: '/terms',          fileName: 'terms_of_service' },
    { id: 'privacy',            label: isEnglish ? 'Privacy Policy'        : 'Gizlilik Politikası', path: '/privacy',         fileName: 'privacy_policies' },
    { id: 'communityGuidelines',label: isEnglish ? 'Community Guidelines'  : 'Topluluk Kuralları',  path: '/community',       fileName: 'honor_code' },
    { id: 'contentPolicy',      label: isEnglish ? 'Content Policy'        : 'İçerik Politikası',   path: '/content-policy',  fileName: 'content_policy' },
    { id: 'cookies',            label: isEnglish ? 'Cookie Policy'         : 'Çerez Politikası',    path: '/cookies',         fileName: 'cookies_policy' },
    { id: 'copyright',          label: isEnglish ? 'Copyright Policy'      : 'Telif Hakkı İhlali',  path: '/copyright',       fileName: 'copyright_policy' },
    { id: 'childSafety',        label: isEnglish ? 'Child Safety'          : 'Çocuk Güvenliği',     path: '/child-safety',    fileName: 'child_safety' },
  ];

  const findTabIndex = (tabId: string): number => {
    const index = tabs.findIndex(tab => tab.id === tabId);
    return index >= 0 ? index : 0;
  };

  const [activeTab, setActiveTab] = useState(findTabIndex(defaultTab));

  // URL değiştiğinde sekmeyi güncelle
  useEffect(() => {
    setActiveTab(findTabIndex(defaultTab));
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  }, [defaultTab]);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    navigate(tabs[index].path);
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  // Active tab metadata
  const activeTabData = tabs[activeTab];

  return (
    <div className="tp-page">

      {/* ── Header ── */}
      <header className="tp-header">
        <div className="tp-header-inner">
          <a href="/" className="tp-logo-link">
            <div className="tp-logo-mark">
              <div className="tp-logo-inner">
                <span className="tp-logo-g">G</span>
              </div>
            </div>
            <span className="tp-logo-txt">GetSpeakHub</span>
          </a>
          <button className="tp-back-link" onClick={() => navigate('/')}>
            <ArrowLeft size={15} />
            {isEnglish ? 'Back to home' : 'Ana sayfaya dön'}
          </button>
        </div>
      </header>

      {/* ── Hero ── */}
      <div className="tp-hero">
        <div className="tp-hero-inner">
          <div className="tp-hero-label">
            <Shield size={12} />
            {isEnglish ? 'Legal' : 'Hukuki'}
          </div>
          <h1 className="tp-hero-title">
            {isEnglish ? 'Terms & Policies' : 'Koşullar ve Politikalar'}
          </h1>
          <p className="tp-hero-sub">
            {isEnglish
              ? 'Effective for all GetSpeakHub users worldwide. Last reviewed May 2026.'
              : 'Tüm GetSpeakHub kullanıcıları için geçerlidir. Son inceleme: Mayıs 2026.'}
          </p>
        </div>
      </div>

      {/* ── Layout ── */}
      <div className="tp-layout">

        {/* ── Sidebar ── */}
        <aside className="tp-sidebar">
          <div className="tp-sidebar-card">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                className={`tp-tab-btn ${activeTab === index ? 'active' : ''}`}
                onClick={() => handleTabChange(index)}
                role="tab"
                aria-selected={activeTab === index}
                id={`terms-tab-${index}`}
                aria-controls={`terms-tabpanel-${index}`}
              >
                <span className="tp-tab-icon">{TAB_ICONS[tab.id]}</span>
                {tab.label}
                <span className="tp-tab-dot" />
              </button>
            ))}
          </div>

          <div className="tp-sidebar-updated">
            <p className="tp-sidebar-updated-lbl">
              {isEnglish ? 'Last Updated' : 'Son Güncelleme'}
            </p>
            <p className="tp-sidebar-updated-val">
              {isEnglish ? 'May 1, 2026' : '1 Mayıs 2026'}
            </p>
          </div>
        </aside>

        {/* ── Content ── */}
        <div className="tp-content" ref={contentRef}>
          {tabs.map((tab, index) => (
            <TabPanel key={tab.id} value={activeTab} index={index}>
              <div className={`tp-panel ${activeTab === index ? 'active' : ''}`}>

                {/* Badge */}
                <div className="tp-content-badge">
                  {TAB_ICONS[tab.id]}
                  {tab.label}
                </div>

                {/* Title */}
                <h1 className="tp-content-title">{tab.label}</h1>

                {/* Meta */}
                <div className="tp-content-meta">
                  <span>
                    <Calendar size={13} />
                    {isEnglish ? 'Effective: May 1, 2026' : 'Yürürlük: 1 Mayıs 2026'}
                  </span>
                  <span>
                    <Clock size={13} />
                    {isEnglish ? '~5 min read' : '~5 dk okuma'}
                  </span>
                </div>

                {/* Divider */}
                <div className="tp-content-divider" />

                {/* HTML content */}
                <div className="tp-html-content">
                  <HtmlContent
                    fileName={tab.fileName}
                    language={currentLanguage}
                    className="tp-html-content"
                  />
                </div>

              </div>
            </TabPanel>
          ))}
        </div>

      </div>

      {/* ── Footer ── */}
      <footer className="tp-footer">
        <div className="tp-footer-inner">
          <a href="/" className="tp-footer-logo">
            <div className="tp-logo-mark">
              <div className="tp-logo-inner">
                <span className="tp-logo-g">G</span>
              </div>
            </div>
            <span className="tp-footer-logo-txt">GetSpeakHub</span>
          </a>
          <div className="tp-footer-links">
            {tabs.map((tab, index) => (
              <a key={tab.id} href={tab.path} onClick={e => { e.preventDefault(); handleTabChange(index); }}>
                {tab.label}
              </a>
            ))}
          </div>
          <span className="tp-footer-copy">© 2026 GetSpeakHub, Inc.</span>
        </div>
      </footer>

    </div>
  );
}

export default TermsPage;
